/**
 * `automation-draft` — the wiring half of the agreement card (TV1-2423).
 *
 * `AutomationDraftBlock` is deliberately presentational: it takes `onSubmit` as a
 * prop so it stays renderable in jsdom with no provider and no backend. That left
 * the registration site as the only place a handler could come from — and nothing
 * passed one, so every Enviar click hit `onSubmit?.()` and evaporated. This
 * component IS that handler, kept next to the block rather than inside it so the
 * one network call the card makes is visible from where the block is registered.
 *
 * ─── One request id per CARD, not per click ─────────────────────────────────
 * `automationRequestId` is client-minted and becomes the aggregate id of
 * `SubmitAutomationRequest`, so a retried submit of the same draft resolves to the
 * SAME Automation Request instead of a second one
 * (`teselly-ml-and-ai-library/src/capabilities/automation/submit_automation_request.ts`).
 * That property only holds if the id is minted ONCE per mounted card — hence the
 * lazy `useState` initialiser rather than a `uuidv7()` inside the click handler.
 *
 * ─── Why the endpoint and not a command ─────────────────────────────────────
 * `POST /ai-assistant/automation-requests/submit` runs the SAME
 * `submit_automation_request` execute the model would, behind the same gate. A
 * direct `backend.commands.*` dispatch would reach the aggregate with no gate at
 * all, so the gate would have to be copied — and the copy is the one that drifts.
 *
 * ─── An ACCEPTED submit spends the control; a refusal does not ──────────────
 * Submit is disabled while in flight and after the server ACCEPTS: the seller has
 * agreed once, and the card must not read as if the agreement still needs making.
 * Everything that is NOT an `ok: true` re-enables it, for one reason — no
 * Automation Request is known to exist, so clicking again is the right thing to
 * do, and a repeat carries the same card-minted id so it cannot become a second
 * request:
 *  - a REFUSAL, which arrives as a resolved 200 carrying `{ ok: false, message }`
 *    (`aiAssistant.endpoint.ts`: "Returns 200 with the tool's return value
 *    verbatim, refusals included"), so "resolved" alone is NOT acceptance;
 *  - a THROW, or a 200 body this client cannot narrow.
 * The refusal stays on its own code path — the narrowing in the `try`, the
 * transport failure in the `catch` — because merging them would render the
 * server's words for a network drop, or bury the one sentence that says what to
 * fix under generic copy.
 */

import { useCallback, useState } from 'react';
import { uuidv7 } from 'uuidv7';
import useBackend from '@contexts/useBackend/useBackend';
import type { MessageBlockRendererProps } from '@domains/aiAssistant/state/messageBlocks';
import {
  type AutomationDraftPayload,
  type SubmitAutomationRequestOutcome,
  toSubmitAutomationRequestBody,
  toSubmitAutomationRequestOutcome
} from '@domains/aiAssistant/state/automationDraft';
import { AutomationDraftBlock } from './AutomationDraftBlock';

export const AutomationDraftBlockConnected = ( props: MessageBlockRendererProps ) => {
  // `useBackend()` is `BackendContextType | null`, and `backend` itself is
  // undefined until the provider's effect has built the client. Both are read
  // defensively: a click before then must do nothing, never throw off a render.
  const backendContext = useBackend();
  const backend = backendContext?.backend;

  const [automationRequestId] = useState(() => uuidv7());
  const [ submitting, setSubmitting ] = useState( false );
  const [ submitted, setSubmitted ] = useState( false );
  const [ outcome, setOutcome ] = useState<SubmitAutomationRequestOutcome | undefined>( undefined );

  const onSubmit = useCallback( async ( payload: AutomationDraftPayload ) => {
    if ( !backend || submitting || submitted ) return;

    setSubmitting( true );
    // A retry starts with no verdict: the previous refusal or failure describes a
    // call that is over, and leaving it up next to a spinner reads as if the card
    // were reporting on the attempt now in flight.
    setOutcome( undefined );
    try {
      // The TYPED generated operation on purpose: this call site is the only
      // gate on the request body's shape, and reaching it through an index
      // signature would delete that gate and turn a missing field into a 400
      // nobody sees.
      const response = await backend.post.ai_assistant_submit_automation_request(
        toSubmitAutomationRequestBody( payload, automationRequestId )
      );
      // RESOLVED is not ACCEPTED. The narrowing owns the discriminator; only an
      // `ok: true` spends the control, so a refusal leaves Submit clickable and
      // the `finally` below re-enables it by clearing `submitting` alone.
      const resolved = toSubmitAutomationRequestOutcome( response );
      setOutcome( resolved );
      if ( resolved.kind === 'submitted' ) setSubmitted( true );
    } catch {
      // A THROW never produced an answer, so there are no server words to show
      // and the card falls back to its own copy (as it also does for a 200 the
      // narrowing cannot read). Swallowed rather than rethrown — a click handler
      // that throws takes the chat down with it — but no longer SILENT, which is
      // what made slice 1's click feel like a void.
      setOutcome({ kind: 'failed' });
    } finally {
      setSubmitting( false );
    }
  }, [ backend, submitting, submitted, automationRequestId ]);

  return (
    <AutomationDraftBlock
      {...props}
      onSubmit={onSubmit}
      submitting={submitting}
      submitted={submitted}
      outcome={outcome}
    />
  );
};
